import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ClockIcon, ShareIcon } from '@heroicons/react/24/outline'
import type { SpinSummary } from '../../../Generated/SpinSummary.ts'
import { fetchSpinSummary } from '../../../Repository/Visualization/SpinMeta.js'
import { SpinLineageModal } from './SpinLineageModal.js'
import { SpinInsightNudge } from './SpinInsightNudge.js'
import { AssignmentsSection, BinCountriesSection, DefinitionSection, HeroSection, ScoresSection } from './SpinSummarySections.js'

const SHARE_FEEDBACK_MS = 2000

/**
 * Public, shareable single-SPIN page (`/spins/:spinId`): the SPIN's headline taxon, every database's
 * assignment, unity and reach, and its definition — never the member OTUs, which are the admin-only
 * "what fed it" view. Backed by `/api/spins/{id}`, open to every signed-in user.
 */
export const SpinSummaryPage = () => {
  const { spinId } = useParams<{ spinId: string }>()
  const [summary, setSummary] = useState<SpinSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [lineageOpen, setLineageOpen] = useState(false)

  useEffect(() => {
    if (!spinId) return
    let cancelled = false
    setLoading(true)
    fetchSpinSummary(spinId)
      .then((s) => !cancelled && setSummary(s))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [spinId])

  const share = () => {
    void navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), SHARE_FEEDBACK_MS)
    })
  }

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold">SPIN</h1>
          <p className="font-mono text-sm text-gray-500 break-all">{spinId}</p>
        </div>
        <div className="flex gap-2">
          {(summary?.predecessorSpinIds ?? []).length > 0 && (
            <button type="button" className="btn btn-sm btn-outline gap-1" onClick={() => setLineageOpen(true)}>
              <ClockIcon className="w-4 h-4" />
              Lineage
            </button>
          )}
          <button type="button" className="btn btn-sm btn-outline gap-1" onClick={share}>
            <ShareIcon className="w-4 h-4" />
            {copied ? 'Link copied' : 'Share'}
          </button>
        </div>
      </header>

      <SpinInsightNudge />

      {spinId && lineageOpen && <SpinLineageModal spinId={spinId} isOpen onClose={() => setLineageOpen(false)} />}

      {loading && <p className="text-gray-500">Loading…</p>}
      {!loading && !summary && <p className="text-error">SPIN not found.</p>}

      {!loading && summary && (
        <div className="space-y-4 bg-emerald-50/70 rounded-2xl p-3 md:p-5">
          <HeroSection summary={summary} />
          <AssignmentsSection summary={summary} />
          <div className="grid gap-4 xl:grid-cols-2">
            <DefinitionSection summary={summary} />
            <div className="space-y-4">
              <ScoresSection summary={summary} />
              <BinCountriesSection summary={summary} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
